import { useEffect } from 'react';
import { apm } from '@elastic/apm-rum';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';

import { setFeatures } from '../../../actions/features';
import { getDeviceIDFromCookie } from '../../../common/device-cookie';
import { getConfig } from '../../../config';
import { getSessionCompanyId } from '../../../selectors/session';
import { FeatureFlag, FeatureFlagCache } from '../types';
import useRefetchMeForV2Migration from './useRefetchMeForV2Migration';

export default function useCacheFeatureFlags() {
  const buildId = process.env.BUILD_ID || 'development';
  const deviceId = getDeviceIDFromCookie();
  const companyId: string = useSelector(getSessionCompanyId) || 'anonymous';
  const key = 'feature-flags-cache';
  const { FEATURE_FLAGS_CACHE } = getConfig();

  function getCachedFeatureFlags(): FeatureFlag[] | undefined {
    try {
      const cacheStr = window.localStorage.getItem(key);
      if (!FEATURE_FLAGS_CACHE.enabled || !cacheStr) {
        return undefined;
      }

      const cache: FeatureFlagCache = JSON.parse(cacheStr);
      if (
        cache.buildId !== buildId ||
        cache.deviceId !== deviceId ||
        cache.companyId !== companyId ||
        isNaN(cache.time) ||
        !Array.isArray(cache.data)
      ) {
        return undefined;
      }

      // The unit of duration is minute
      const stale =
        dayjs().diff(new Date(cache.time), 'minute', true) >=
        FEATURE_FLAGS_CACHE.duration;

      return stale ? undefined : cache.data;
    } catch (error) {
      apm.captureError(error as Error);
      return undefined;
    }
  }

  function useCache(data: FeatureFlag[]) {
    const dispatch = useDispatch();
    useEffect(() => {
      dispatch(setFeatures(data));
    }, [data, dispatch]);
    // Same v2 re-fetch as in useGetFeatureFlags, but for the cached-flags path.
    useRefetchMeForV2Migration(data);
  }

  function setCache(data: FeatureFlag[]) {
    if (!FEATURE_FLAGS_CACHE?.enabled) return;
    window.localStorage.setItem(
      key,
      JSON.stringify({
        buildId,
        deviceId,
        companyId,
        time: Date.now(),
        data,
      })
    );
  }

  return { getCachedFeatureFlags, useCache, setCache };
}
